import { useMemo, useState } from 'react';
import { Button, Table, Dialog, ConfirmDialog, Input, Select, Badge, Alert, Tabs, useToast } from '@dreamtree-org/twreact-ui';
import { Download, Pencil, Trash2, Search } from 'lucide-react';
import { PageHeader, QueryState, EmptyState } from '@/components';
import { errorText, formatINR } from '@/lib/format';
import { useQuery } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import { useCrudList, useCrudWrite } from '../hooks';
import { paiseToRupees } from '../lib/money';
import { retailFor, marginError, tightestCap, validateReprice, repricePayload, chargeRange, importSummary } from '../lib/sourcing';

/**
 * Sourcing (B16-V4, dropshipper stores only, D57/D70): find products vendors
 * offer for dropshipping and import them into your catalog as drafts at your
 * margin, within each supplier's cap (D54). Selling them opens with order
 * routing; until then they stay drafts. Prices and stock follow the supplier.
 */
const TABS = [{ key: 'find', label: 'Find products' }, { key: 'sourced', label: 'Sourced' }];

export default function Sourcing() {
  const can = useCan();
  const { toast } = useToast();
  const write = useCrudWrite();
  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState({ q: '', product_type: '', brand: '', page: 1 });
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState([]);
  const [importing, setImporting] = useState(null); // { margin }
  const [repricing, setRepricing] = useState(null); // { row, form }
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState('');

  const catalog = useQuery('crud', { model: 'SourcedListing', action: 'catalog', data: filters }, { select: (r) => r, enabled: tab === 0 });
  const sourced = useCrudList('SourcedListing', { orderBy: { column: 'id', direction: 'desc' }, limit: 1000 });
  const listings = useCrudList('Listing', { select: ['id', 'title', 'price_paise', 'status'], limit: 2000 });
  const byListing = useMemo(() => new Map((listings.data || []).map((l) => [String(l.id), l])), [listings.data]);
  const listingOf = (s) => byListing.get(String(s.listing_id)) || {};

  const facets = (catalog.data && catalog.data.facets) || { product_type: [], brand: [] };
  const option = (f) => ({ value: f.value, label: `${f.value} (${f.count})` });
  const setFilter = (k, v) => { setPicked([]); setFilters((f) => ({ ...f, [k]: v || '', page: 1 })); };

  const cap = tightestCap(picked);
  const marginProblem = importing ? marginError(importing.margin) || (cap != null && Number(importing.margin) > cap ? `The tightest supplier cap in your selection is ${cap}%` : null) : null;

  const runImport = async () => {
    setError('');
    try {
      const r = await write.run({ model: 'SourcedListing', action: 'import', data: { items: picked.map((o) => ({ company_id: o.company_id, listing_id: o.listing_id })), margin_pct: Number(importing.margin) } });
      toast.success('Import finished', importSummary((r && r.data) || []));
      setImporting(null);
      setPicked([]);
      catalog.reload();
      sourced.reload();
      listings.reload();
    } catch (ex) {
      setError(errorText(ex));
    }
  };
  const runReprice = async () => {
    setError('');
    try {
      await write.run({ model: 'SourcedListing', action: 'reprice', data: repricePayload(repricing.row.id, repricing.form) });
      toast.success('Price saved', listingOf(repricing.row).title || '');
      setRepricing(null);
      sourced.reload();
      listings.reload();
    } catch (ex) {
      setError(errorText(ex));
    }
  };
  const runRemove = async () => {
    setError('');
    try {
      await write.run({ model: 'SourcedListing', action: 'remove', data: { id: removing.id } });
      toast.success('Removed', listingOf(removing).title || '');
      setRemoving(null);
      sourced.reload();
      listings.reload();
    } catch (ex) {
      setError(errorText(ex));
    }
  };

  const repriceErrors = repricing ? validateReprice(repricing.form, repricing.row.wholesale_paise, repricing.row.max_margin_pct) : {};
  const setForm = (k, v) => setRepricing((r) => ({ ...r, form: { ...r.form, [k]: v } }));
  const sourcedActions = [
    can('dropship', 'update') && { name: 'reprice', label: 'Reprice', icon: <Pencil size={14} />, inline: true, onClick: ({ row }) => { setError(''); setRepricing({ row, form: { mode: 'margin', margin: String(Number(row.margin_pct)), price: paiseToRupees(listingOf(row).price_paise) } }); } },
    can('dropship', 'delete') && { name: 'remove', label: 'Remove', icon: <Trash2 size={14} />, onClick: ({ row }) => { setError(''); setRemoving(row); } },
  ].filter(Boolean);

  const page = catalog.data || {};
  return (
    <div className="space-y-6">
      <PageHeader
        title="Sourcing"
        subtitle="Import products other stores supply. You pay their wholesale price and keep your margin, up to the cap each supplier sets; they ship under your store's name."
      />
      <Alert variant="info" title="Imported products stay drafts for now">Selling dropshipped products opens with order routing. Until then you can import, price and prepare them; prices and stock follow the supplier.</Alert>
      {error && !importing && !repricing && !removing ? <Alert variant="error" title="Not saved" dismissible onDismiss={() => setError('')}>{error}</Alert> : null}

      <Tabs index={tab} onChange={setTab} orientation="horizontal" variant="line" animated={false}>
        <Tabs.List className="overflow-x-auto whitespace-nowrap border-b border-[var(--app-border)]">
          {TABS.map((t) => <Tabs.Tab key={t.key} className="min-h-[44px]">{t.label}</Tabs.Tab>)}
        </Tabs.List>
      </Tabs>

      {tab === 0 ? (
        <div className="space-y-4">
          <form className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr_auto]" onSubmit={(e) => { e.preventDefault(); setFilter('q', search.trim()); }}>
            <Input aria-label="Search supplier products" placeholder="Search by product, brand or supplier" value={search} onChange={(e) => setSearch(e.target.value)} maxLength={80} />
            <Select aria-label="Product type" placeholder="All types" options={facets.product_type.map(option)} value={filters.product_type} onChange={(v) => setFilter('product_type', v)} closeOnSelect />
            <Select aria-label="Brand" placeholder="All brands" options={facets.brand.map(option)} value={filters.brand} onChange={(v) => setFilter('brand', v)} searchable closeOnSelect />
            <Button type="submit" variant="outline" leftIcon={<Search size={16} />}>Search</Button>
          </form>
          {can('dropship', 'import') ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm opacity-70">{picked.length ? `${picked.length} selected` : 'Select products to import.'}</span>
              <Button leftIcon={<Download size={16} />} disabled={!picked.length} onClick={() => { setError(''); setImporting({ margin: cap != null ? String(Math.min(20, cap)) : '20' }); }}>Import as drafts</Button>
            </div>
          ) : null}
          <QueryState query={catalog}>
            {(res) => ((res.data || []).length ? (
              <Table
                data={res.data.map((o) => ({ ...o, id: `${o.company_id}:${o.listing_id}` }))}
                columns={[
                  { key: 'title', label: 'Product', render: (o) => <span className="font-medium">{o.title}{o.imported ? <Badge variant="secondary" size="sm" className="ml-2">Imported</Badge> : null}</span> },
                  { key: 'store_name', label: 'Supplier', hideOnMobile: true },
                  { key: 'wholesale_paise', label: 'Wholesale', render: (o) => formatINR(o.wholesale_paise) },
                  { key: 'max_retail', label: 'You can charge', render: (o) => chargeRange(o.wholesale_paise, o.max_margin_pct, (p) => formatINR(p)) },
                  { key: 'handling_days', label: 'Ships in', render: (o) => `${o.handling_days} d`, hideOnMobile: true },
                  { key: 'qty_available', label: 'Stock', hideOnMobile: true },
                ]}
                selectable={can('dropship', 'import')}
                onSelectionChange={(rows) => setPicked(rows.filter((o) => !o.imported))}
                withAction={false}
                showSerial={false}
                showColumnPicker={false}
                showLimitSelector={false}
                showReloadButton={false}
                serverSide
                pagination
                pageSize={page.page_size || 24}
                totalRecords={page.total || 0}
                pageNumber={page.page || 1}
                onPageChange={(p) => { setPicked([]); setFilters((f) => ({ ...f, page: p })); }}
              />
            ) : (
              <EmptyState title="No supplier products found">{filters.q || filters.brand || filters.product_type ? 'Try other words or clear the filters.' : 'No supplier offers products for dropshipping to your store yet.'}</EmptyState>
            ))}
          </QueryState>
        </div>
      ) : (
        <QueryState query={sourced}>
          {(rows) => (rows.length ? (
            <Table
              data={rows}
              columns={[
                { key: 'listing', label: 'Product', render: (s) => <span className="font-medium">{listingOf(s).title || `Listing ${s.listing_id}`}</span> },
                { key: 'source_store_name', label: 'Supplier', render: (s) => s.source_store_name || s.source_store_slug, hideOnMobile: true },
                { key: 'wholesale_paise', label: 'Wholesale', render: (s) => formatINR(s.wholesale_paise), hideOnMobile: true },
                { key: 'margin_pct', label: 'Margin', render: (s) => `${Number(s.margin_pct)}%${s.max_margin_pct == null ? '' : ` (cap ${Number(s.max_margin_pct)}%)`}` },
                { key: 'price', label: 'Your price', render: (s) => formatINR(listingOf(s).price_paise) },
                {
                  key: 'status', label: 'Status',
                  render: (s) => (s.status === 'unavailable'
                    ? <span><Badge variant="warning" size="sm">Unavailable</Badge>{s.unavailable_reason ? <span className="block text-xs opacity-70">{s.unavailable_reason}</span> : null}</span>
                    : <Badge variant="secondary" size="sm">Draft</Badge>),
                },
              ]}
              actions={sourcedActions}
              replaceDefaultActions
              withAction={sourcedActions.length > 0}
              showSerial={false}
              showColumnPicker={false}
              globalSearch
              pagination
              pageSize={25}
            />
          ) : (
            <EmptyState title="Nothing sourced yet">Find products on the first tab and import them as drafts.</EmptyState>
          ))}
        </QueryState>
      )}

      <Dialog isOpen={!!importing} onClose={() => setImporting(null)} title={`Import ${picked.length} product${picked.length === 1 ? '' : 's'} as drafts`} size="md">
        {importing ? (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (!marginProblem) runImport(); }}>
            <Input label="Your margin (%)" inputMode="decimal" value={importing.margin} onChange={(e) => setImporting({ margin: e.target.value })} error={marginProblem || undefined}
              helperText={cap != null ? `Your price is the wholesale price plus this margin. The tightest supplier cap here is ${cap}%.` : 'Your price is the wholesale price plus this margin. None of these suppliers set a cap.'} required />
            {!marginProblem && picked.length === 1 ? <p className="text-sm opacity-80">Your price: {formatINR(retailFor(picked[0].wholesale_paise, importing.margin))}</p> : null}
            {error ? <Alert variant="error" title="Not imported">{error}</Alert> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setImporting(null)}>Cancel</Button>
              <Button type="submit" loading={write.loading} disabled={Boolean(marginProblem)}>Import</Button>
            </div>
          </form>
        ) : null}
      </Dialog>

      <Dialog isOpen={!!repricing} onClose={() => setRepricing(null)} title="Reprice" size="md">
        {repricing ? (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (!Object.keys(repriceErrors).length) runReprice(); }}>
            <p className="text-sm opacity-80">Wholesale {formatINR(repricing.row.wholesale_paise)}; you can charge {chargeRange(repricing.row.wholesale_paise, repricing.row.max_margin_pct, (p) => formatINR(p))}.</p>
            <Select label="Set by" options={[{ value: 'margin', label: 'Margin %' }, { value: 'price', label: 'Price (₹)' }]} value={repricing.form.mode} onChange={(v) => setForm('mode', v || 'margin')} closeOnSelect />
            {repricing.form.mode === 'price'
              ? <Input label="Your price (₹)" inputMode="decimal" value={repricing.form.price} onChange={(e) => setForm('price', e.target.value)} error={repriceErrors.price} />
              : <Input label="Your margin (%)" inputMode="decimal" value={repricing.form.margin} onChange={(e) => setForm('margin', e.target.value)} error={repriceErrors.margin}
                  helperText={repriceErrors.margin ? undefined : `Your price: ${formatINR(retailFor(repricing.row.wholesale_paise, repricing.form.margin))}`} />}
            {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRepricing(null)}>Cancel</Button>
              <Button type="submit" loading={write.loading} disabled={Object.keys(repriceErrors).length > 0}>Save</Button>
            </div>
          </form>
        ) : null}
      </Dialog>

      <ConfirmDialog
        isOpen={!!removing}
        title="Remove this sourced product?"
        description="Its draft listing leaves your catalog. You can import it again later."
        confirmLabel="Remove"
        loading={write.loading}
        onCancel={() => setRemoving(null)}
        onConfirm={runRemove}
      />
    </div>
  );
}
